/**
 * src/lib/nav/projectNav.js — TICKET-UPLOAD-PROGRESS-UX (CEO 2026-06-07).
 *
 * Race-free "open project P, on tab T" request, used by the History stepper's
 * "Open in History" deep-link (and reusable by anything that needs to jump into
 * a project workspace from outside the Projects view).
 *
 * Why a module-level request instead of the existing `acn:search-open` event:
 * ProjectsView only mounts when the Projects tab is active, and it subscribes to
 * navigation AFTER it mounts. A one-shot event dispatched while you're on another
 * tab fires before ProjectsView exists → it's missed (the known "lands on the
 * list, not the detail" bug). This request PERSISTS until ProjectsView consumes
 * it: the caller sets it + switches to the Projects tab; ProjectsView then
 * consumes it on mount (and on a projects-loaded retry), or immediately via the
 * subscription when it's already mounted. peek/consume keeps it idempotent — the
 * request is only cleared once the project is actually found + opened.
 */

let pending = null;
const listeners = new Set();

/** Request that ProjectsView open `projectId`, optionally on internal `tab`. */
export function requestProjectOpen(projectId, { tab = null } = {}) {
  if (!projectId) return;
  pending = { projectId, tab };
  listeners.forEach((l) => {
    try { l(); } catch { /* spine-emit-ok: a listener error must not break navigation */ }
  });
}

/** Read the pending request without clearing it (so a failed open can retry). */
export function peekProjectOpen() { return pending; }

/** Read + clear the pending request (call only once you've handled it). */
export function consumeProjectOpen() { const p = pending; pending = null; return p; }

/** Subscribe to new requests; returns an unsubscribe fn. */
export function subscribeProjectOpen(cb) { listeners.add(cb); return () => listeners.delete(cb); }

/** Test helper. */
export function _resetProjectNav() { pending = null; listeners.clear(); }
